import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import PageTopBar from "./PageTopBar";
import {
  DASHBOARD,
  UPLOAD,
  EBAY,
  EBAY_SYNC,
  BATCHES,
  GLOBAL_PRESETS,
  TITLE_BUILDER,
  POLICIES,
  PROFILE_SETTINGS,
  PAYMENT_DETAILS,
  TEST_PAGE,
} from "../../routes/routes";

const PAGE_TITLES = [
  { path: `${BATCHES}/`, isPrefix: true, title: "Batches" },
  { path: BATCHES, title: "Batches" },
  { path: DASHBOARD, title: "Dashboard" },
  { path: `${EBAY_SYNC}/`, isPrefix: true, title: "eBay Sync" },
  { path: EBAY_SYNC, title: "eBay Sync" },
  { path: UPLOAD, title: "Upload" },
  { path: `${GLOBAL_PRESETS}/`, isPrefix: true, title: "Global Presets" },
  { path: GLOBAL_PRESETS, title: "Global Presets" },
  { path: TITLE_BUILDER, title: "Default Title Format" },
  { path: POLICIES, title: "Policies & Tax" },
  { path: PROFILE_SETTINGS, title: "Profile Settings" },
  { path: PAYMENT_DETAILS, title: "Payment Details" },
  { path: TEST_PAGE, title: "Test Page" },
  { path: EBAY, title: "eBay" },
];

function getPageTitle(pathname) {
  if (pathname === "/batches/1/cards" || /^\/batches\/\d+\/cards/.test(pathname)) {
    return "Cards";
  }
  if (pathname === `${GLOBAL_PRESETS}/new`) {
    return "New Global Preset";
  }
  if (pathname.startsWith(`${GLOBAL_PRESETS}/edit/`)) {
    return "Edit Global Preset";
  }
  for (const entry of PAGE_TITLES) {
    if (entry.isPrefix ? pathname.startsWith(entry.path) : pathname === entry.path) {
      return entry.title;
    }
  }
  return "Dashboard";
}

// Styled Components
const LayoutContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  margin-top: 0;
  position: relative;
  background: #001c49;
`;

const ContentArea = styled.main`
  flex: 1;
  min-height: 0;
  margin-left: ${(props) => (props.$sidebarCollapsed ? "80px" : "240px")}; /* Account for fixed sidebar */
  background: #001c49;
  height: 100%;
  width: ${(props) =>
    props.$sidebarCollapsed
      ? "calc(100% - 80px)"
      : "calc(100% - 240px)"}; /* Subtract sidebar width from total width */
  overflow-x: auto; /* Allow horizontal scrolling if needed */
  display: flex;
  flex-direction: column;
  transition: margin-left 0.2s ease, width 0.2s ease;

  @media (max-width: 1024px) {
    margin-left: 0;
    width: 100%;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-height: 0;
  background: #f9fafb;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  @media (max-width: 1024px) {
    padding: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;



function Layout({ children, showSidebar = true, showFooter = false }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);

  // Close sidebar on mobile/tablet when window is resized to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sidebarCollapsed", String(next));
      return next;
    });
  };

  return (
    <LayoutContainer>
      {/* Navbar temporarily hidden - moved to Sidebar */}
      {/* <Navbar 
        onToggleMenu={toggleSidebar} 
        isMenuOpen={sidebarOpen}
      /> */}
      
      <MainContent>
        {showSidebar && (
          <Sidebar
            isOpen={sidebarOpen}
            onToggle={toggleSidebar}
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebarCollapse}
          />
        )}

        <ContentArea $sidebarCollapsed={showSidebar && sidebarCollapsed}>
          <ContentWrapper>
            <PageTopBar title={pageTitle} />
            <ScrollArea>{children}</ScrollArea>
          </ContentWrapper>
        </ContentArea>
      </MainContent>
      
      {showFooter && <Footer />}
    </LayoutContainer>
  );
}

export default Layout;
